/**
 * Path separator.
 * @type {RegExp}
 */
const PATH_SEPARATOR = /\./;

/**
 *
 * @param {Object} object
 * @param {String} keyPath
 * @returns {*}
 */
export function getValueFromPath(object, keyPath) {
    try {
        return keyPath.split(PATH_SEPARATOR).reduce((o, i) => o[i], object);
    } catch (error) {
        return undefined;
    }
}

/**
 * Pick given keys from given object.
 * @param  {Object} o    Object where properties are picked
 * @param  {Array}  keys List of wanted keys, in path format
 * @return {Object}      Object with only asked keys
 */
export function pick(o, keys = []) {
    const newObj = {};

    keys.forEach((key) => {
        const value = getValueFromPath(o, key);

        if (typeof value === 'undefined') {
            return;
        }

        const paths = key.split(PATH_SEPARATOR);
        const lastIndex = paths.length - 1;

        paths.reduce((previous, current, i) => {
            if (i === lastIndex) {
                // eslint-disable-next-line no-param-reassign
                previous[current] = value;
                return previous;
            }
            if (typeof previous[current] === 'undefined') {
                // eslint-disable-next-line no-param-reassign
                previous[current] = {};
            }
            return previous[current];
        }, newObj);
    });

    return newObj;
}

/**
 * Check if two items are deeply equals.
 * @param  {*} o1 First item
 * @param  {*} o2 Second item
 * @return {bool} True is they are deeply equal, false otherwise
 */
export function deepEqual(o1, o2) {
    if (o1 === o2) {
        return true;
    }

    if (typeof o1 !== typeof o2) {
        return false;
    }

    const typeO1 = typeof o1;

    if (typeO1 === 'number' && isNaN(o1) && isNaN(o2)) {
        return true;
    }

    if (
        typeO1 === 'undefined' ||
        typeO1 === 'null' ||
        typeO1 === 'string' ||
        typeO1 === 'number' ||
        typeO1 === 'boolean'
    ) {
        return false;
    }

    if (o1 instanceof Date && o2 instanceof Date) {
        return o1.getTime() === o2.getTime();
    }

    if (Array.isArray(o1)) {
        if (o1.length !== o2.length) {
            return false;
        }

        for (let i = 0, size = o1.length; i < size; i += 1) {
            if (!deepEqual(o1[i], o2[i])) {
                return false;
            }
        }
    }

    const o1Keys = Object.keys(o1).sort();
    const o2Keys = Object.keys(o2).sort();

    if (o1Keys.join(',') !== o2Keys.join(',')) {
        return false;
    }

    for (let i = 0, size = o1Keys.length; i < size; i += 1) {
        const key = o1Keys[i];
        if (!deepEqual(o1[key], o2[key])) {
            return false;
        }
    }

    return true;
}

/**
 * Remove key from object with prefixed values.
 * @param {Object} obj Object to clean
 * @param {String|Array<String>} prefixes Prefixes key to remove
 * @return {Object} Same object passed, for chaining
 */
/* eslint-disable no-param-reassign */
export function removePrefixedValues(obj, prefixes) {
    let usedPrefixes;
    if (Array.isArray(prefixes)) {
        usedPrefixes = prefixes;
    } else {
        usedPrefixes = [prefixes];
    }

    Object.keys(obj)
        .filter((key) => usedPrefixes.some((prefix) => key.startsWith(prefix)))
        .forEach((key) => delete obj[key]);

    return obj;
}
/* eslint-enable no-param-reassign */

/**
 * Remove private key (starting with underscore) and null/undefined values of first-level object (no deep). Mutate object.
 * @param {Object} obj Object to clean
 * @return {Object} Same object passed, for chaining
 */
/* eslint-disable no-param-reassign */
export function removePrivateAndNullValues(obj) {
    removePrefixedValues(obj, '_');
    Object.keys(obj).forEach((key) => {
        if (obj[key] === null || obj[key] === undefined) {
            delete obj[key];
        }
    });

    return obj;
}
/* eslint-enable no-param-reassign */

/**
 * Determine whether or not given parameter is a function.
 * @param  {Any} fn   A possible function
 * @return {Boolean}  True if function, false otherwise
 */
export function isFunction(fn) {
    return fn instanceof Function;
}
